import React, { useContext } from 'react'
import { FieldRenderProps } from 'react-final-form'
import { Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ClassValue } from 'classnames/types'
import classNames from 'classnames'
import TextField from '@material-ui/core/TextField'
import { Mutators } from 'final-form-arrays'
import AddCircleIcon from '@material-ui/icons/AddCircle'
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle'
import styles from '../../styles'
import { ResponsiveContext } from '../../utils/context/responsiveContext'
import { WHITE } from '../../theme'

const useStyles = makeStyles(
	(theme) => (
		{
			...styles(theme),
			marginBottom: {
				marginBottom: 10
			},
			textFieldStyle: {
				width: '100%',
				padding: '8px 8px 8px 16px',
				boxSizing: 'border-box',
				backgroundColor: WHITE,

				'& input': {
					...styles(theme).text1,
					fontSize: 18,
					fontWeight: 500,
					lineHeight: 1.33
				},
				'& textarea': {
					...styles(theme).text1,
					fontSize: 18,
					fontWeight: 500,
					opacity: 0.7,
					lineHeight: 1.33
				}
			},
			errorStyle: {
				color: '#B92D00',
				fontSize: 10
			},
			errorColor: {
				color: '#B92D00 !important'
			},
			errorBorder: {
				border: '1px solid #B92D00'
			},
			textFieldContainer: {
				marginTop: 10
			},
			buttonStyle: {
				display: 'flex',
				cursor: 'pointer',

				'&:first-child': {
					marginRight: 10
				},
				'&:last-child': {
					marginLeft: 10
				},
				'& svg': {
					fill: theme.palette.blue.main,
					height: 30,
					width: 30
				}
			},
			minDisabled : {
				'&:first-child': {
					cursor: 'initial',
					opacity: '0.5',
					'& svg': {
						fill: '#CCC',
					}
				},
			},
			maxDisabled : {
				'&:last-child': {
					cursor: 'initial',
					opacity: '0.5',
					'& svg': {
						fill: '#CCC',
					}
				},
			},
			inputCenter:{
				'& input': {
					textAlign: 'center'
				}
			}
		}
	)
)

interface AcceptingProps {
	id: string,
	label: string,
	mutators?: Mutators,
	mutatorFieldName?: string,
	mutatorValue?: any
	min: number,
	max: number
}

type NbChildrenFieldProps =
	FieldRenderProps<number>
	& AcceptingProps

const NbChildrenField: React.FC<NbChildrenFieldProps> = (
	{
		input,
		meta: {
			touched,
			error
		},
		id,
		label,
		mutators,
		mutatorFieldName = '',
		mutatorValue,
		min,
		max
	}
) => {
	const classes = useStyles()
	const { isLargeScreen } = useContext(ResponsiveContext)

	const labelClass: ClassValue = classNames(
		classes.text7,
		classes.marginBottom,
		classes.colorBlue,
		{
			[classes.errorColor]: touched && error
		}
	)

	const textFieldClass: ClassValue = classNames(
		classes.textFieldStyle,
		{
			[classes.inputCenter]: !isLargeScreen,
			[classes.errorBorder]: touched && error
		}
	)

	const buttonStyle: ClassValue = classNames(
		classes.buttonStyle,
		{[classes.minDisabled]: input.value <= min},
		{[classes.maxDisabled]: input.value >= max},
	)

	// Fonction pour enlever un élément au FieldArray associé (s'il y en a un)
	const lessChild = () => {
		if (input.value > min) {
			mutators?.pop(mutatorFieldName)
			input.onChange(input.value - 1)
		}
	}

	// Fonction pour ajouter un élément au FieldArray associé (s'il y en a un)
	const moreChild = () => {
		if (input.value < max) {
			mutators?.push(mutatorFieldName, mutatorValue)
			input.onChange(input.value + 1)
		}
	}

	return (
		<Grid container item xs={12} className={classes.textFieldContainer}>
			<Grid item xs={12} className={labelClass}>{label}</Grid>

			<Grid container item xs={12} alignItems="center">
				<Grid item className={buttonStyle} onClick={lessChild}>
					<RemoveCircleIcon />
				</Grid>

				<Grid item xs={9} md={4}>
					<TextField
						id={id}
						disabled
						color="primary"
						className={textFieldClass}
						InputProps={{ disableUnderline: true }}
						{...input}
					/>
				</Grid>

				<Grid item className={buttonStyle} onClick={moreChild}>
					<AddCircleIcon />
				</Grid>
			</Grid>

			{
				touched &&
				((error && <Grid item xs={12} className={classes.errorStyle}>{error}</Grid>))
			}
		</Grid>
	)
}

export default NbChildrenField
